import { AuthToken } from '@cobliteam/api-lib/src/account/session';
import { Location } from '@cobliteam/common-types';
import type { NamespaceKeys } from '@cobliteam/i18n';
import { i18nConfig } from '@cobliteam/i18n';
import { importPathAssociationPage, importPathAssociationRouter } from '@cobliteam/path-association/src/routes';
import { importGoogleMapsProvider } from '../providers/GoogleMapsProvider/importGoogleMapsProvider';
import { importHomePage } from '../routes/home';
import { importMonitoringPage, importMonitoringPageV2 } from '../routes/monitoring';

type InitialRoutePreload = {
  importPage: () => Promise<unknown>;
  // Route namespaces outside BASE_NAMESPACES: each one suspends the page once more when it mounts.
  namespaces: NamespaceKeys[];
  needsGoogleMaps?: boolean;
};

const GOOGLE_MAPS_ORIGINS = ['https://maps.googleapis.com', 'https://maps.gstatic.com'];

// The busiest initial loads; others still load their chunk when the route renders.
const PRELOADS: Partial<Record<string, InitialRoutePreload>> = {
  [Location.EMPTY]: { importPage: importHomePage, namespaces: ['home'] },
  [Location.HOME]: { importPage: importHomePage, namespaces: ['home'] },
  [Location.MONITORING]: { importPage: importMonitoringPage, namespaces: ['map'], needsGoogleMaps: true },
  [Location.MONITORING_V2]: { importPage: importMonitoringPageV2, namespaces: ['map'], needsGoogleMaps: true },
  // O Router e a página são lazy em cadeia; pedir os dois evita a segunda viagem depois dos portões.
  [Location.PATH_ASSOCIATION]: { importPage: () => Promise.all([importPathAssociationRouter(), importPathAssociationPage()]), namespaces: ['path-association'] },
};

const preconnect = (origin: string) => {
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = origin;
  document.head.appendChild(link);
};

/**
 * Starts the initial route's chunk, translations and map connection while session, user data and
 * flags are still resolving; without it they only start once every boot gate has rendered the route.
 */
export const preloadInitialRoute = (pathname = window.location.pathname) => {
  const preload = PRELOADS[pathname];
  if (!preload) return;
  // Without a token the route redirects to login, so the chunk would be wasted.
  if (!localStorage.getItem(AuthToken.ACCESS_TOKEN)) return;

  // A failed preload is harmless: lazyWithRetries retries the same import when the route renders.
  preload.importPage().catch(() => undefined);
  i18nConfig.loadNamespaces(preload.namespaces).catch(() => undefined);

  if (preload.needsGoogleMaps) {
    importGoogleMapsProvider().catch(() => undefined);
    GOOGLE_MAPS_ORIGINS.forEach(preconnect);
  }
};
